import {LanguageComponent} from '../../language/language.component';
import {Component, computed, effect, signal, Signal, TemplateRef} from '@angular/core';
import {
  File,
  Folder,
  Mutation,
  Organization,
  ProcessStateEnum,
  Query,
  Tag,
  Track,
  TrackType
} from '../../../types-repository';
import {Apollo, gql, onlyCompleteData} from 'apollo-angular';
import {CombinedGraphQLErrors} from '@apollo/client/errors';
import {Constants} from '../../../globals';
import {catchError, distinctUntilChanged, filter, map, skipWhile, switchMap, tap} from 'rxjs/operators';
import {EMPTY, Observable} from 'rxjs';
import {errorText} from '../../../graphql/error-link';
import {throwOnError} from '../../../graphql/gql-load';
import {BannerService} from '../../../banner/banner.service';
import {FILE_DETAILS, GET_PARENTS, START_GENERATE_STILLS} from '../../../queries-repository';
import {ActivatedRoute, RouterModule} from '@angular/router';
import {FileFolderLinkPipe} from '../../file-folder-link.pipe';
import {BreadCrumb, LogoAreaComponent} from '../../logo-area/logo-area.component';
import {Dialog} from '@angular/cdk/dialog';
import {NgxGalleryAnimation, NgxGalleryImage, NgxGalleryModule, NgxGalleryOptions} from '@vinlos/ngx-gallery';
import {ContextService} from '../../../services/context.service';
import {ProcessService} from '../../../services/process.service';
import {DoButtonComponent} from '../../do-button/do-button.component';
import {S3LinkComponent} from '../../s3-link/s3-link.component';
import {MessageFieldComponent} from '../../do-button/message-field.component';
import {HeadlineSectionComponent} from '../../headline-section/headline-section.component';
import {CommonModule, JsonPipe, NgTemplateOutlet} from '@angular/common';
import {toSignal} from '@angular/core/rxjs-interop';
import {FormFooterComponent} from '../../form-footer/form-footer.component';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';


@Component({
  selector: 'app-file-details',
  templateUrl: './file-details.component.html',
  styleUrls: ['./file-details.component.scss'],
  imports: [
    LanguageComponent,
    FormFooterComponent,
    S3LinkComponent,
    MessageFieldComponent,
    DoButtonComponent,
    RouterModule,
    HeadlineSectionComponent,
    LogoAreaComponent,
    ReactiveFormsModule,
    FormsModule,
    JsonPipe,
    NgxGalleryModule,
    CommonModule,
    NgTemplateOutlet
  ]
})
export class FileDetailsComponent {
  TRACKTYPES = TrackType
  loading = signal(true);
  /** Why the file could not be loaded; replaces the loading spinner. */
  loadError = signal<string | null>(null);

  file: Signal<File>;
  folder: Signal<Folder>;
  organization: Signal<Organization>
  breadcrumps: Signal<BreadCrumb[]>;

  // Permission signals - initialized in constructor after file signal is set up
  canUpdateFile: Signal<boolean>;
  canRefreshFFProbe: Signal<boolean>;
  canRefreshBasicQc: Signal<boolean>;
  canRefreshExtra: Signal<boolean>;
  canRefreshMediaInfo: Signal<boolean>;

  languages = Constants.languages.slice();

  ffprobe: Array<any>;
  readonly warnings = computed(() => {
    const file = this.file();
    if (!file?.warnings) return [];
    return file.warnings.map(e => JSON.parse(e));
  });
  extra_info: any;
  mediainfo: string;
  mediainfo_json: Array<any>;
  tracks: Track[];
  tags: Tag[];

  readonly CRITICAL_QC_CODES = new Set([
    'SUBS_MISSING_FONT',
    'SUBS_MISSING_GLYPHS',
    'SUBS_EMPTY_SOURCE',
    'SUBS_CANNOT_READ_SOURCE',
    'SUBS_UNSUPPORTED_FORMAT',
  ]);

  warningOccurrences: any[] = [];

  getLevel(warning: any): 'info' | 'warning' | 'error' {
    if (warning.level === 'info' || warning.level === 'warning' || warning.level === 'error') {
      return warning.level;
    }
    return this.CRITICAL_QC_CODES.has(warning.code) ? 'error' : 'warning';
  }

  readonly visibleWarnings = computed(() =>
    this.warnings().filter(w => this.getLevel(w) !== 'info')
  );

  readonly criticalWarnings = computed(() =>
    this.visibleWarnings().filter(w => this.getLevel(w) === 'error')
  );

  readonly groupedNonCriticalWarnings = computed(() => {
    const groups = new Map<string, any[]>();
    for (const w of this.visibleWarnings()) {
      if (this.getLevel(w) !== 'warning') continue;
      if (!groups.has(w.code)) groups.set(w.code, []);
      groups.get(w.code)!.push(w);
    }
    return Array.from(groups.entries()).map(([code, items]) => ({ code, items }));
  });

openWarningOccurrences(items: any[], template: TemplateRef<any>) {
    this.warningOccurrences = items;
    this.dialog.open(template);
  }

  glacier: boolean;
  stills: NgxGalleryImage[] = [];
  has_video: boolean;

  private assetPath$: Observable<string>;

  galleryOptions: NgxGalleryOptions[] = [
    {
      width: '100%',
      thumbnailsColumns: 6,
      height: '800px',
      imageAnimation: NgxGalleryAnimation.Fade
    },
    {
      breakpoint: 992,
      height: '600px',
    },
    {
      breakpoint: 768,
      height: '400px',
    }

  ];

  constructor(
    private apollo: Apollo,
    private route: ActivatedRoute,
    protected dialog: Dialog,
    private contextService: ContextService,
    private banner: BannerService,
    private processService: ProcessService
  ) {
    this.organization = contextService.organization
    this.assetPath$ = this.route.paramMap.pipe(
      map(pm => pm.get('assetpath') || ''),
      distinctUntilChanged(),
      tap(() => {
        this.loading.set(true);
        this.loadError.set(null);
      })
    )
    this.folder = toSignal(this.assetPath$.pipe(
      filter(assetPath => !!assetPath),
      switchMap(assetPath => this.apollo.watchQuery<Query>({
        query: GET_PARENTS,
        variables: {
          id: `s3://${assetPath.split('/').slice(0, -1).join('/')}/`
        }
      }).valueChanges.pipe(
        throwOnError(),
        onlyCompleteData(),
        map(res => res.data.folder),
        // Only the breadcrumbs depend on it: say so, and keep the route stream alive.
        catchError(e => {
          this.banner.open(`Could not load the folder path: ${errorText(e)}`, ['Dismiss']);
          return EMPTY;
        }),
      ))
    ))
    this.breadcrumps = computed(() => {
      console.log('breadcrumps');
      const fs = this.folder();
      if (fs) {
        const crumbs: BreadCrumb[] = [
          {'commands': `/o/${this.organization().id}`, 'display': 'Content Platform'} as BreadCrumb,
          {'commands': `/o/${this.organization().id}/files`, 'display': 'Files'} as BreadCrumb,
        ];
        fs.parents.forEach(f => {
          crumbs.push({
            'commands': new FileFolderLinkPipe(this.contextService).transform(f),
            'display': f.name
          } as BreadCrumb);
        });
        crumbs.push({
          'commands': new FileFolderLinkPipe(this.contextService).transform(fs),
          'display': fs.name
        })
        return crumbs;
      } else {
        return [];
      }
    })

    this.file = toSignal(
      this.assetPath$.pipe(
        filter(assetPath => !!assetPath),
        switchMap(assetPath => this.apollo.watchQuery<Query>({
          query: FILE_DETAILS,
          variables: {
            id: `s3://${assetPath}`
          },
          errorPolicy: 'all',
          nextFetchPolicy: 'cache-only'
        }).valueChanges.pipe(
          tap(response => {
            // Refused outright (no file at all): the spinner turns into the reason.
            if (response.error && !response.data?.file) {
              this.loadError.set(errorText(response.error));
            }
          }),
          skipWhile((response) => !response.data?.file),
          map(response => {
            const f = Object.assign({}, response.data.file);
            this.loading.set(false)
            if (CombinedGraphQLErrors.is(response.error)) {
              this.banner.open('Error retrieving metadata. Data might not be complete.', ['Close'])
              for (const err of response.error.errors) {
                if (err.path[0] === 'file' && err.path[1] === 'tags') {
                  f.tags = []
                }
                if (err.path[0] === 'file' && err.path[1] === 'tracks') {
                  f.tracks = []
                }
                if (err.path[0] === 'file' && err.path[1] === 'mediainfo') {
                  f.mediainfo = ''
                }
                if (err.path[0] === 'file' && err.path[1] === 'mediainfo_json') {
                  f.mediainfo_json = [JSON.stringify({'message': 'no mediainfo data'})]
                }
                if (err.path[0] === 'file' && err.path[1] === 'ffprobe') {
                  f.ffprobe = [JSON.stringify({'message': 'no ffprobe data'})]
                }
              }
            }
            return f as File;
          }),
          catchError(e => {
            this.loadError.set(errorText(e));
            return EMPTY;
          }),
        )))
    )

    // Initialize permission signals after file signal is set up
    // We read organization() to establish it as a dependency, since isAllowed() depends on org policies
    this.canUpdateFile = computed(() => {
      const file = this.file();
      this.contextService.organization();
      return this.contextService.isAllowed(
        'contentplatform:repository:Mutation/UpdateFile',
        'contentplatform:file:input.file_id/' + (file?.id || '')
      )();
    });
    this.canRefreshFFProbe = computed(() => {
      const file = this.file();
      this.contextService.organization();
      return this.contextService.isAllowed(
        'contentplatform:repository:Mutation/RefreshFFProbe',
        'contentplatform:file:input.file_id/' + (file?.id || '')
      )();
    });
    this.canRefreshBasicQc = computed(() => {
      const file = this.file();
      this.contextService.organization();
      return this.contextService.isAllowed(
        'contentplatform:repository:Mutation/RefreshBasicQc',
        'contentplatform:file:input.file_id/' + (file?.id || '')
      )();
    });
    this.canRefreshExtra = computed(() => {
      const file = this.file();
      this.contextService.organization();
      return this.contextService.isAllowed(
        'contentplatform:repository:Mutation/RefreshExtra',
        'contentplatform:file:input.file_id/' + (file?.id || '')
      )();
    });
    this.canRefreshMediaInfo = computed(() => {
      const file = this.file();
      this.contextService.organization();
      return this.contextService.isAllowed(
        'contentplatform:repository:Mutation/RefreshMediaInfo',
        'contentplatform:file:input.file_id/' + (file?.id || '')
      )();
    });

    effect(() => {
      const file = this.file();
      if (!file) {
        return;
      }
      if (file.ffprobe !== null) {
        this.ffprobe = file.ffprobe.map(e => JSON.parse(e))
      } else {
        this.ffprobe = [{'message': 'no ffprobe data'}]
      }
      if (file.extra_info !== null) {
        this.extra_info = JSON.parse(file.extra_info)
      } else {
        this.extra_info = []
      }
      if (file.mediainfo_json !== null) {
        this.mediainfo_json = file.mediainfo_json.map(e => JSON.parse(e))
      } else {
        this.mediainfo_json = [{'message': 'no mediainfo data'}]
      }
      this.mediainfo = file.mediainfo
      this.has_video = file.tracks !== null ? file.tracks.filter(t => t.track_type === TrackType.Video).length > 0 : false;
      this.stills = file.stills.map((still, i) => {
        return {
          small: still.url,
          big: still.url,
          medium: still.url,
        }
      }).slice(0, 100)
      this.glacier = file.archived;
      if (file.tags !== null) {
        this.tags = file.tags;
        if (this.tags == null || this.tags.length === 0) {
          this.tags = []
        }
        this.tags = this.tags.map(t => {
          return {'key': t.key, 'value': t.value}
        });
      }
      if (file.tracks !== null) {
        for (const track of file.tracks) {
          if (this.languages.filter(a => a.id === track.language).length === 0) {
            this.languages = [].concat(...this.languages, {'id': track.language, 'name': track.language})
          }
        }

        this.tracks = file.tracks.map((v) => {
          return {
            'index': v.index,
            'enabled': v.enabled,
            'language': v.language,
            'track_type': v.track_type
          }
        });
      } else {
        this.tracks = []
      }

    })

  }

  refreshTracks(button: DoButtonComponent) {
    this.apollo.mutate<Mutation>({
      mutation: gql`mutation RefreshTracks($id: ID!) {
        refreshTracks(input: {file_id: $id, force: true}) {
          file {
            id
            tracks {
              index
              enabled
              language
              track_type
            }
          }
        }
      }
      `,
      variables: {
        id: this.file().id
      },
      fetchPolicy: 'network-only'
    }).subscribe({
      next: res => {
        button.done('Track data reset.')
      },
      error: err => {
        button.done('Reset failed: ' + err.message, {severity: 'cl-alert-danger'})
      }
    });
  }

  onAddTag(event: Event) {
    this.tags.push({
      key: '',
      value: ''
    })
  }

  save(button: DoButtonComponent) {
    const tag_inputs = [];
    for (const tag of this.tags) {
      if (tag.key !== null && tag.key !== '') {
        let item = ''
        item += '{\n';
        item += `  key: "${tag.key}"`
        item += `  value:" ${tag.value}"`
        item += '}\n'
        tag_inputs.push(item)
      }
    }

    const track_input = [];
    for (const track of this.tracks) {
      let item = ''
      item += '{\n';
      item += `  index: ${track.index}`
      item += `  track_type: ${track.track_type}`
      item += `  language: "${track.language}"`
      item += `  enabled: ${track.enabled}`
      item += '}\n'
      track_input.push(item)
    }
    this.apollo.mutate<Mutation>({
      mutation: gql`mutation UpdateFileDetails($id: ID!) {
        updateFile(input: {
          file_id: $id
          tags: [${tag_inputs.join(',\n')}
          ]
          tracks: [${track_input.join(',\n')}
          ]
        }) {
          file {
            id
            tags {
              key
              value
            }
            tracks {
              index
              enabled
              language
              track_type
            }
          }
        }
      }
      `,
      variables: {
        id: this.file().id,
      }
    }).subscribe({
      next: res => {
        button.done('Changes saved')
        const file = res.data.updateFile.file;
        this.tracks = file.tracks;
        this.tags = res.data.updateFile.file.tags;
      },
      error: err => {
        button.done('Workflow metadata was not updated: ' + err.message, {severity: 'cl-alert-danger'})
      }
    })

  }


  refresh_ffprobe(button: DoButtonComponent) {
    this.apollo.mutate<Mutation>({
      mutation: gql`mutation Refresh_ffprobe($id: ID!) {
        refreshFFProbe(input: {file_id: $id, force: true}) {
          file {
            id
            ffprobe
          }
        }
      }
      `,
      fetchPolicy: 'network-only',
      variables: {
        id: this.file().id
      }
    }).subscribe({
      next: res => {
        button.done('FFprobe refreshed')
      },
      error: err => {
        button.done('FFprobe refreshed', {severity: 'cl-alert-danger'})
      }
    })

  }

  refresh_basic_qc(button: DoButtonComponent) {

    this.apollo.mutate<Mutation>({
      mutation: gql`mutation RefreshBasicQc($id: ID!) {
        refreshBasicQc(input: {file_id: $id, force: true}) {
          file {
            id
            warnings
          }
        }
      }
      `,
      fetchPolicy: 'network-only',
      variables: {
        id: this.file().id
      }
    }).subscribe({
      next: () => button.done('Auto QC updated'),
      error: err => button.done('Auto QC was not updated: ' + err.message, {
        severity: 'cl-alert-danger'
      })
    })
  }

  refresh_extra_info(button: DoButtonComponent) {
    this.apollo.mutate<Mutation>({
      mutation: gql`mutation RefreshExtra($id: ID!) {
        refreshExtra(input: {file_id: $id, force: true}) {
          file {
            id
            extra_info
          }
        }
      }
      `,
      fetchPolicy: 'network-only',
      variables: {
        id: this.file().id
      }
    }).subscribe({
      next: () => button.done('Extra info updated'),
      error: err => button.done('Extra info was not updated: ' + err.message, {severity: 'cl-alert-danger'})
    })
  }

  openDialog(dialog: TemplateRef<any>) {
    this.dialog.open(dialog)
  }

  refresh_mediainfo(button: DoButtonComponent) {
    this.apollo.mutate<Mutation>({
      mutation: gql`mutation refreshMediaInfo($id: ID!) {
        refreshMediaInfo(input: {file_id: $id, force: true}) {
          file {
            id
            mediainfo_json
            mediainfo
          }
        }
      }
      `,
      variables: {
        id: this.file().id
      },
      fetchPolicy: 'network-only'
    }).subscribe(
      {
        next: () => {
          button.done('MediaInfo updated')
        },
        error: err => {
          this.dialog.closeAll();
          button.done('MediaInfo was not updated: ' + err.message, {severity: 'cl-alert-danger'})
        }
      })
  }

  generate_stills(button: DoButtonComponent) {
    this.apollo.mutate<Mutation>({
      mutation: START_GENERATE_STILLS,
      variables: {
        file_id: this.file().id
      }
    }).pipe(
      switchMap(d => this.processService.observeProcess(d.data.generate_stills, {
        successMessage: `Still generation for ${this.file().name} succeeded.`,
        failureMessage: `Still generation for ${this.file().name} failed.`
      })),
      skipWhile(process => {
        return process.state === ProcessStateEnum.InProgress
      }),
      switchMap(() => this.apollo.query<Query>({
        query: FILE_DETAILS,
        variables: {
          id: this.file().id
        },
        errorPolicy: 'all',
        fetchPolicy: 'network-only'
      }))
    ).subscribe({
        complete: () => {
          button.done()
        },
        error: err => button.done(`Still generation failed: ${errorText(err)}`, {severity: 'cl-alert-danger'}),
      }
    );
  }

  /**
   * Copies `text` and reports the outcome on the button. `writeText` is asynchronous and can be
   * rejected (denied permission, insecure context), so the button must only confirm once it
   * resolved — reporting "Copied" up front confirms a copy that may never have happened.
   */
  private copy_to_clipboard(button: DoButtonComponent, text: string) {
    if (!navigator.clipboard?.writeText) {
      button.done('Copy failed: clipboard not available', {severity: 'cl-alert-danger'})
      return
    }
    navigator.clipboard.writeText(text).then(
      () => button.done('Copied'),
      err => button.done('Copy failed: ' + (err?.message ?? String(err)), {severity: 'cl-alert-danger'})
    )
  }

  copy_ffprobe(button: DoButtonComponent) {
    this.copy_to_clipboard(button, JSON.stringify(this.ffprobe, null, 2))
  }

  copy_warnings(button: DoButtonComponent) {
    this.copy_to_clipboard(button, JSON.stringify(this.warnings(), null, 2))
  }

  hasKeys(obj: any, ...keys: string[]): boolean {
    return obj != null && typeof obj === 'object' && keys.every(k => k in obj);
  }

  formatGlyph(glyph: string): string {
    if (!glyph) return '(empty)';
    const codepoints = Array.from(glyph)
      .map(c => `U+${c.codePointAt(0)!.toString(16).toUpperCase().padStart(4, '0')}`);
    const isPrintable = Array.from(glyph).every(c => {
      const cp = c.codePointAt(0)!;
      return cp > 0x1F && cp !== 0x7F && !(cp >= 0x80 && cp <= 0x9F);
    });
    return isPrintable ? glyph : codepoints.join(' ');
  }

  copy_extra_info(button: DoButtonComponent) {
    this.copy_to_clipboard(button, JSON.stringify(this.extra_info, null, 2))
  }

  copy_mediainfo(button: DoButtonComponent) {
    // mediainfo is MediaInfo's plain-text report, displayed as-is. JSON-encoding it would paste a
    // quoted single line with escaped newlines instead of the readable report.
    this.copy_to_clipboard(button, this.mediainfo)
  }

  copy_mediainfo_json(button: DoButtonComponent) {
    this.copy_to_clipboard(button, JSON.stringify(this.mediainfo_json, null, 2))
  }

}
